import React from "react";
import { getFileType } from "@/utils/fileProcessing";
import { FileText, Image, File, X, Clock, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export interface FileProgress {
  file: File;
  progress: number;
  status: 'pending' | 'processing' | 'completed' | 'error';
  extractedText?: string;
  error?: string;
}

interface MultipleFilePreviewProps {
  filesProgress: FileProgress[];
  onRemoveFile: (index: number) => void;
  disabled: boolean;
  isProcessing: boolean;
}

const MultipleFilePreview: React.FC<MultipleFilePreviewProps> = ({
  filesProgress,
  onRemoveFile,
  disabled,
  isProcessing
}) => {
  const getFileIcon = (file: File) => {
    const type = getFileType(file);
    
    switch (type) {
      case "pdf":
        return <FileText className="h-6 w-6 text-red-500" />;
      case "png":
      case "jpg":
      case "jpeg":
        return <Image className="h-6 w-6 text-blue-500" />;
      case "doc":
      case "txt":
      default:
        return <File className="h-6 w-6 text-idoc-500" />;
    }
  };

  const getStatusIcon = (status: FileProgress['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'processing':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'error':
        return <X className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: FileProgress['status']) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="text-xs">Concluído</Badge>;
      case 'processing':
        return <Badge variant="secondary" className="text-xs">Processando</Badge>;
      case 'error':
        return <Badge variant="destructive" className="text-xs">Erro</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">Aguardando</Badge>;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getTotalProgress = () => {
    if (filesProgress.length === 0) return 0;
    const total = filesProgress.reduce((sum, fp) => sum + fp.progress, 0);
    return Math.round(total / filesProgress.length);
  };

  const completedFiles = filesProgress.filter(fp => fp.status === 'completed').length;
  const totalFiles = filesProgress.length;

  return (
    <div className="space-y-4">
      {/* Header with overall progress */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium">
            Arquivos Selecionados ({completedFiles}/{totalFiles})
          </h3>
          {isProcessing && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3 animate-spin" />
              Processando...
            </div>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          {getTotalProgress()}% concluído
        </div>
      </div>

      {/* Overall progress bar */}
      {isProcessing && (
        <Progress value={getTotalProgress()} className="h-2" />
      )}

      {/* Files list */}
      <div className="grid gap-3 max-h-60 overflow-y-auto">
        {filesProgress.map((fileProgress, index) => (
          <div
            key={`${fileProgress.file.name}-${index}`}
            className="flex items-center space-x-3 p-3 border rounded-md bg-secondary/30 transition-all"
          >
            {/* File icon */}
            <div className="flex-shrink-0">
              {getFileIcon(fileProgress.file)}
            </div>
            
            {/* File info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-medium truncate">
                  {fileProgress.file.name}
                </p>
                <div className="flex items-center gap-2">
                  {getStatusIcon(fileProgress.status)}
                  {getStatusBadge(fileProgress.status)}
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatFileSize(fileProgress.file.size)}</span>
                <span>{fileProgress.progress}%</span>
              </div>
              
              {/* Progress bar for individual file */}
              <Progress 
                value={fileProgress.progress} 
                className="h-1 mt-1"
              />
              
              {/* Error message */}
              {fileProgress.status === 'error' && fileProgress.error && (
                <p className="text-xs text-red-500 mt-1">
                  {fileProgress.error}
                </p>
              )}
              
              {/* Extracted text info */}
              {fileProgress.status === 'completed' && fileProgress.extractedText && (
                <p className="text-xs text-green-600 mt-1">
                  {fileProgress.extractedText.length} caracteres extraídos
                </p>
              )}
            </div>
            
            {/* Remove button */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => onRemoveFile(index)}
              disabled={disabled || fileProgress.status === 'processing'}
              className="flex-shrink-0 h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Processing summary */}
      {completedFiles > 0 && (
        <div className="text-xs text-muted-foreground p-2 bg-muted/50 rounded">
          <strong>Resumo:</strong> {completedFiles} arquivo(s) processado(s) com sucesso.
          {filesProgress.some(fp => fp.extractedText) && (
            <span>
              {' '}Total de caracteres extraídos: {
                filesProgress
                  .filter(fp => fp.extractedText)
                  .reduce((sum, fp) => sum + (fp.extractedText?.length || 0), 0)
                  .toLocaleString()
              }
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MultipleFilePreview;